/**
 * /best-ai-note-taking-app 的中文版本(zh)。
 *
 * 编辑角度:一份诚实的、按用途分类的 AI 笔记 app 清单,而不是变相广告。
 * 大方点名各品类里真正强的工具(Notion AI、Obsidian、Mem、Reflect、
 * Granola、Otter),诚实承认它们各自的长处。
 *
 * 关键转折:Draftlize 不是笔记 app,这页就直说。单独一节点出——有一类
 * 搜"最好的 AI 笔记 app"的人,其实根本不需要笔记 app:如果你"记的"是
 * 产品决策、需要 AI agent 读写、并在依赖变化时自动标记 stale,那你找错
 * 了品类,你要的是决策工作台(Draftlize)。诚实的做法是把这类人指向对的
 * 品类,而不是假装 Draftlize 是更好的笔记 app——它不是,我们也不这么说。
 *
 * 英文版本见 BestAiNoteTakingApp.tsx。
 */
import { MarketingShell } from './MarketingShell';

export function BestAiNoteTakingAppZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/best-ai-note-taking-app"
      documentTitle="最好的 AI 笔记 App(2026)· Draftlize"
      documentDescription="一份诚实的 2026 年 AI 笔记 app 清单,按你到底在记什么来分类:全能笔记(Notion AI、Obsidian)、AI 原生笔记(Mem、Reflect)、会议记录(Granola、Otter)。以及一个例外——什么时候你其实根本不需要笔记 app。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 860 }}>
          <span className="label label-amber">指南 · AI 笔记 App</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(38px, 4.8vw, 60px)', lineHeight: 1.12, margin: '12px 0 24px' }}
          >
            最好的 AI 笔记 app,<br/>
            <em>以及一种你其实不该用它的情况。</em>
          </h1>
          <p className="lede">
            大多数「最好的 AI 笔记 app」清单只是一堆 logo 的堆砌。这篇不是。我们也做这个邻域的产品,但先把话说清楚:Draftlize <strong>不是</strong>笔记 app,我们也不会假装它是一个更好的笔记 app。所以这是一份真正诚实的地图——按你到底在记什么,列出各品类里真正强的 AI 笔记工具、谁赢得每个类别;然后,点出那一个重要的例外:当你「记」的东西决定了笔记 app 根本就是错的工具。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">一句话总结</span>
            <h2 className="serif">没有哪个 AI 笔记 app 通吃全部。<em>按你记什么来选。</em></h2>
          </div>
          <p>「最好的 AI 笔记 app」完全取决于你在记什么。团队 wiki、个人第二大脑、一墙的会议转录,要的是完全不同的工具。下面是每一行谁赢——以及最底下那几行,诚实的答案根本不是笔记 app。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>你在记什么</th>
              <th>真正强的选择</th>
              <th className="ours">这是笔记 app 该干的活吗?</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>团队 wiki、文档与结构化页面</td><td className="yes">Notion AI</td><td className="ours yes">是 —— 用 Notion</td></tr>
            <tr><td>个人第二大脑、双链笔记</td><td className="yes">Obsidian、Mem、Reflect</td><td className="ours yes">是 —— 用这些</td></tr>
            <tr><td>会议记录与转录</td><td className="yes">Granola、Otter</td><td className="ours yes">是 —— 用这些</td></tr>
            <tr><td>快速捕捉、跨笔记 AI 检索</td><td className="yes">Mem、Reflect、Notion AI</td><td className="ours yes">是 —— 任选其一</td></tr>
            <tr><td>需要 AI agent 读写的产品决策</td><td className="no">没有笔记 app 能做</td><td className="ours partial">不是 —— 那是决策工作台</td></tr>
            <tr><td>改一个决策,自动标出哪些已 stale</td><td className="no">笔记只会原地不动</td><td className="ours partial">不是 —— 你要的是依赖图</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">按品类,<em>诚实地讲。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">全能笔记</h3>
            <p>
              <strong>Notion AI</strong> —— 团队 wiki 的默认选项;灵活的数据库、协作页面,AI 起草和问答就内建在你文档已经在的地方。<br/>
              <strong>Obsidian</strong> —— 本地优先、纯文本 Markdown、双链密集,深受「用图谱思考」的人喜爱;AI 通过插件接入,完全在你掌控之下。<br/>
              <em>诚实地说:</em>如果你想要一个灵活的地方来写、整理、检索一切,这两个都真的很强,很难选错。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">AI 原生笔记</h3>
            <p>
              <strong>Mem</strong> —— 会自我组织的笔记;你把想法丢进去,AI 帮你浮现和串联,几乎不用手动归档。<br/>
              <strong>Reflect</strong> —— 快、无摩擦的网状笔记,带反向链接和 AI 助手,专为每日记录和快速捕捉打磨。<br/>
              <em>诚实地说:</em>它们从底层就是 AI 优先设计的。如果你讨厌归档、想让工具替你整理,这类会很出彩。这是真心话。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">会议记录</h3>
            <p>
              <strong>Granola</strong> —— 录下并把你自己潦草的笔记增强成干净的会议纪要,不用一个笨重的机器人加入通话。<br/>
              <strong>Otter</strong> —— 实时转录、说话人标注、可搜索的存档,以及每场会议的 AI 摘要。<br/>
              <em>诚实地说:</em>如果你的问题是「开会时记不过来」,这正是你要的品类。这些事 Draftlize 一件都不做。
            </p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">有一种情况,笔记 app 是<em>错的工具。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            下面是诚实的部分。有些搜「最好的 AI 笔记 app」的人,其实并不是在找笔记 app——他们只是没见过自己真正需要的那个品类。如果你是这种情况,解法不是一个更聪明的笔记 app,而是另一种工具。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">你「记」的其实是决策</h3>
            <p>如果你反复回看的笔记是产品决策——我们要做什么、为什么、依据是什么、否决了哪些备选——笔记 app 把它们存成会慢慢过期的散文。Draftlize 把每条都捕捉成结构化卡片:决策、依据、依赖,可用 URL 和 ID 寻址。那不是记笔记,那是决策记录。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">一处改动应当把相关项标 stale</h3>
            <p>这正是你已经离开「笔记 app 地界」的信号。一条笔记不可能知道它记下的某个决策已经不再成立——它只会原地待着,理直气壮地错下去。Draftlize 的卡片知道谁依赖自己:改了定价决策,所有基于旧价格的 spec 就自动变 <em>stale</em>,就像构建系统会让被改文件的所有下游全部失效一样。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">需要 AI agent 读它、也写它</h3>
            <p>笔记 app 是你写给你自己回看的。Draftlize 的卡片由 AI agent 通过 MCP 来读、也来写——Claude Code、Cursor。给你起草下一份 spec 的 agent 已经知道那些决策,并把新决策写回去。一个笔记 app 没法成为正在干活的 agent 所需要的那种共享的、机器可写的事实源。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          最好的 AI 笔记 app,是最契合你所记内容的那个。如果你记的是 agent 必须帮你保持同步的产品决策——那压根就不是笔记 app。
        </blockquote>
        <cite>Draftlize 不是更好的笔记 app。它是另一个品类:产品决策工作台。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">找错品类了?<em>用 $5 免费试试决策工作台。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账户赠送 $5</span>
          <span>用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            如果你是来找 AI 笔记 app 的,就从上面那些好工具里挑一个——Notion、Obsidian、Mem、Reflect、Granola、Otter。但如果你真正在追踪的是需要保持同步的产品决策,Draftlize 就是那个品类指向的工具。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
